import { useId, useState } from 'react'
import type { FamilyState, Reward } from '@domain/state.ts'
import { childrenOf } from '@domain/state.ts'
import type { MemberId, RewardId } from '@domain/ids.ts'
import { es } from '../strings/es.ts'

type Kind = Reward['kind']

const KINDS: readonly Kind[] = ['screen', 'privilege', 'family', 'social', 'big']

/**
 * The shop, edited by tapping.
 *
 * Until now the catalogue could only come from the seed, which meant it was
 * fixed on the first day — the opposite of what a weekly meeting is for. This is
 * the second way in: the same three operations the assistant can propose, done
 * by hand, through the same commands.
 *
 * A price change says out loud that it lands at the meeting and not tonight,
 * because that is the part a parent will otherwise be surprised by. A price that
 * moved under a child who was saving for it is the fastest way to teach that
 * saving is pointless, so the domain schedules it and this screen explains it.
 */
export function ShopEditor({
  state,
  meetingDay,
  onCreate,
  onReprice,
  onArchive,
  onBack,
}: {
  state: FamilyState
  meetingDay: string
  onCreate: (reward: {
    name: string
    price: number
    kind: Kind
    memberIds: readonly MemberId[]
  }) => Promise<string | null>
  onReprice: (rewardId: RewardId, newPrice: number) => Promise<string | null>
  onArchive: (rewardId: RewardId, reason: string) => Promise<string | null>
  onBack: () => void
}) {
  const children = childrenOf(state)
  const rewards = [...state.rewards.values()].sort((a, b) => a.price - b.price)
  const active = rewards.filter((r) => r.active)
  const gone = rewards.filter((r) => !r.active)

  const [note, setNote] = useState<string | null>(null)
  const [editing, setEditing] = useState<RewardId | null>(null)
  const [newPrice, setNewPrice] = useState(0)

  const [adding, setAdding] = useState(false)
  const [name, setName] = useState('')
  const [price, setPrice] = useState(10)
  const [kind, setKind] = useState<Kind>('privilege')
  const [who, setWho] = useState<ReadonlySet<MemberId>>(new Set(children.map((c) => c.id)))
  const nameId = useId()
  const priceId = useId()

  const add = async (): Promise<void> => {
    if (name.trim().length === 0) {
      setNote(es.shopEditor.needName)
      return
    }
    if (who.size === 0) {
      setNote(es.shopEditor.needWho)
      return
    }
    const error = await onCreate({ name: name.trim(), price, kind, memberIds: [...who] })
    setNote(error ?? es.shopEditor.added)
    if (!error) {
      setName('')
      setAdding(false)
    }
  }

  const reprice = async (reward: Reward): Promise<void> => {
    const error = await onReprice(reward.id, newPrice)
    setNote(error ?? es.shopEditor.repriced(meetingDay))
    if (!error) setEditing(null)
  }

  const remove = async (reward: Reward): Promise<void> => {
    const reason = globalThis.prompt(es.shopEditor.removeReason) ?? ''
    if (reason.trim().length === 0) return
    setNote((await onArchive(reward.id, reason.trim())) ?? es.shopEditor.removed)
  }

  return (
    <div className="mx-auto max-w-2xl px-5 pt-5 pb-16">
      <button type="button" onClick={onBack} className="text-muted mb-5 text-sm font-bold">
        {es.today.back}
      </button>
      <h1 className="mb-1 text-2xl font-extrabold tracking-tight">🛒 {es.shopEditor.title}</h1>
      <p className="text-muted mt-0 mb-5 text-sm">{es.shopEditor.help}</p>

      {note ? (
        <p role="status" className="bg-teal-soft text-teal mb-4 rounded-lg p-3 font-bold">
          {note}
        </p>
      ) : null}

      {active.length === 0 ? <p className="text-muted">{es.shopEditor.empty}</p> : null}

      <ul className="grid list-none gap-3 p-0">
        {active.map((reward) => (
          <li key={reward.id} className="border-rule bg-surface m-0 rounded-xl border p-4">
            <div className="flex items-start gap-4">
              <div className="min-w-0 flex-1">
                <p className="m-0 font-bold">{reward.name}</p>
                <p className="text-muted m-0 mt-1 text-sm">
                  {es.shopEditor.kinds[reward.kind]} ·{' '}
                  {reward.memberIds
                    .map((id) => state.members.get(id)?.name ?? '')
                    .filter(Boolean)
                    .join(', ')}
                </p>
              </div>
              <span className="text-gold shrink-0 text-xl font-extrabold">{reward.price}</span>
            </div>

            {editing === reward.id ? (
              <div className="mt-3">
                <label className="mb-1 block text-sm font-bold" htmlFor={`p-${reward.id}`}>
                  {es.shopEditor.newPrice}
                </label>
                <input
                  id={`p-${reward.id}`}
                  type="number"
                  min={0}
                  max={500}
                  value={newPrice}
                  onChange={(e) => {
                    setNewPrice(Number(e.target.value))
                  }}
                  className="border-rule bg-surface mb-2 w-28 rounded-lg border-2 p-3"
                />
                <p className="text-muted mt-0 mb-3 text-sm">{es.shopEditor.repriceHelp}</p>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      void reprice(reward)
                    }}
                    className="bg-teal rounded-lg px-5 py-3 font-bold text-white"
                  >
                    {es.config.save}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditing(null)
                    }}
                    className="border-rule rounded-lg border-2 px-5 py-3 font-bold"
                  >
                    {es.config.close}
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setNote(null)
                    setNewPrice(reward.price)
                    setEditing(reward.id)
                  }}
                  className="border-rule rounded-lg border-2 px-4 py-2 text-sm font-bold"
                >
                  {es.shopEditor.reprice}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    void remove(reward)
                  }}
                  className="border-rule rounded-lg border-2 px-4 py-2 text-sm font-bold"
                >
                  {es.shopEditor.remove}
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>

      {adding ? (
        <section className="border-rule bg-surface mt-4 rounded-xl border-2 p-4">
          <label htmlFor={nameId} className="mb-1 block text-sm font-bold">
            {es.shopEditor.name}
          </label>
          <input
            id={nameId}
            value={name}
            onChange={(e) => {
              setName(e.target.value)
            }}
            className="border-rule bg-surface mb-3 w-full rounded-lg border-2 p-3"
          />

          <label htmlFor={priceId} className="mb-1 block text-sm font-bold">
            {es.shopEditor.price}
          </label>
          <input
            id={priceId}
            type="number"
            min={0}
            max={500}
            value={price}
            onChange={(e) => {
              setPrice(Number(e.target.value))
            }}
            className="border-rule bg-surface mb-3 w-28 rounded-lg border-2 p-3"
          />

          <fieldset className="m-0 mb-3 border-0 p-0">
            <legend className="mb-2 text-sm font-bold">{es.shopEditor.kind}</legend>
            <div className="flex flex-wrap gap-2">
              {KINDS.map((option) => (
                <button
                  key={option}
                  type="button"
                  aria-pressed={kind === option}
                  onClick={() => {
                    setKind(option)
                  }}
                  className={`border-rule rounded-lg border-2 px-4 py-2 text-sm font-bold ${
                    kind === option ? 'bg-teal-soft text-teal border-teal' : ''
                  }`}
                >
                  {es.shopEditor.kinds[option]}
                </button>
              ))}
            </div>
          </fieldset>

          <fieldset className="m-0 mb-3 border-0 p-0">
            <legend className="mb-2 text-sm font-bold">{es.shopEditor.who}</legend>
            <div className="flex flex-wrap gap-2">
              {children.map((child) => (
                <button
                  key={child.id}
                  type="button"
                  aria-pressed={who.has(child.id)}
                  onClick={() => {
                    setWho((current) => {
                      const next = new Set(current)
                      if (next.has(child.id)) next.delete(child.id)
                      else next.add(child.id)
                      return next
                    })
                  }}
                  className={`border-rule rounded-lg border-2 px-4 py-2 text-sm font-bold ${
                    who.has(child.id) ? 'bg-teal-soft text-teal border-teal' : ''
                  }`}
                >
                  {child.name}
                </button>
              ))}
            </div>
          </fieldset>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => {
                void add()
              }}
              className="bg-teal rounded-lg px-5 py-3 font-bold text-white"
            >
              {es.shopEditor.add}
            </button>
            <button
              type="button"
              onClick={() => {
                setAdding(false)
              }}
              className="border-rule rounded-lg border-2 px-5 py-3 font-bold"
            >
              {es.config.close}
            </button>
          </div>
        </section>
      ) : (
        <button
          type="button"
          onClick={() => {
            setNote(null)
            setAdding(true)
          }}
          className="border-rule mt-4 w-full rounded-xl border-2 border-dashed px-6 py-4 font-bold"
        >
          + {es.shopEditor.add}
        </button>
      )}

      {gone.length > 0 ? (
        <section className="mt-8">
          <h2 className="text-muted mb-3 text-xs font-bold tracking-[0.1em] uppercase">
            {es.shopEditor.archived}
          </h2>
          <ul className="grid list-none gap-2 p-0">
            {gone.map((reward) => (
              <li key={reward.id} className="text-muted m-0 text-sm">
                {reward.name}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  )
}
